import { Theme } from '../types';
import { baseClient } from './baseRequest';
import {
  CollectionReviewStatus,
  CollectionType,
  MarketplaceInfo,
} from './collections';

/**
 * Studio gallery tab filters. These are semantic flags, not field paths — the
 * api's KlydoFilterBuilder owns what each one resolves to. Only the flags below
 * exist server-side; see `getApiFiltersForTab` for the tab mapping.
 */
export type KlydoSearchFilters = {
  /**
   * Resolves onto `public.status`, the stored public serving gate: `true` = public
   * right now, `false` = not public. No date arithmetic on the client — the api
   * (and its hourly cron) owns when the flag flips.
   */
  isPublic?: boolean;
  /**
   * Klydos the artist may still add to a collection: not in one already, and not
   * publicly published. Says nothing about `type` — a klydo needs no approval to
   * join a collection, so DRAFTS ARE INCLUDED. (It used to be `approved`, which
   * required a sealed klydo and therefore hid every draft from the collection
   * picker.)
   *
   * Mutually exclusive with `inCollection`: the api ignores this flag when
   * `inCollection` is also sent.
   */
  availableForCollection?: boolean;
  rejected?: boolean;
  pending?: boolean;
  /** No publication-review record at all — never submitted. */
  notSubmitted?: boolean;
  inCollection?: boolean;
};

/**
 * The klydo discriminator. `Sealed` is a one-way terminal lock meaning
 * "finalized and servable" — reached once via either route (the public review
 * was approved, or the klydo belongs to an approved collection) and never left
 * again. It is not a review verdict, and it does not decide public serving:
 * `public.status` does.
 */
export enum KlydoType {
  Draft = 'DraftKlydo',
  /**
   * The real stored value since the TK-506 migration renamed every document.
   * Ships in the same coordinated release as the api's flip — against a
   * pre-migration api this value matches nothing.
   */
  Sealed = 'SealedKlydo',
}

/**
 * The state of a klydo's PUBLICATION REQUEST. Workflow and audit only — it does
 * NOT gate public serving (`public.status` does), and the state is absent when
 * no request is open. The record itself can outlive it: withdrawing clears the
 * state and the payload but keeps `message`/`comment`, so a rejection stays
 * readable and answerable.
 *
 * No `Draft` member on purpose: "not submitted" is the absence of the STATE,
 * which the `notSubmitted` search filter expresses.
 */
export enum KlydoReviewStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
}

export enum SoundMode {
  OFF = 'OFF',
  ON = 'ON',
  FIRST_LOOP = 'FIRST_LOOP',
}

export enum AlarmType {
  ALARM = 'ALARM',
  REPEAT = 'REPEAT',
  REMINDER = 'REMINDER',
}
export type KlydoAnimationSizes = {
  xs: string;
  s: string;
  m: string;
  l: string;
};

export type KlydoReviewState = {
  state: KlydoReviewStatus;
  message?: string;
  /** The artist's reply to `message`, sent with a resubmission. */
  comment?: string;
  approvedAt?: number;
  updatedAt: number;
  /**
   * The go-live date the artist asked for, as submitted. This is the ONLY place a
   * publication date lives now — read it for "when will it be public", never for
   * "is it public" (`public.status` answers that).
   *
   * `null` means "no date": either none was ever chosen, or a scheduled
   * publication was cancelled — cancelling nulls the date and leaves the verdict
   * `approved`.
   */
  requestPayload?: {
    timestamp: number | null;
  };
};

export type Klydo = {
  type: KlydoType;
  name: string;
  subtitle?: string;
  loopUrl: string;
  theme: Theme;
  id: string;
  creator: string;
  dials: boolean;
  klydoSecureUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  tags?: string[];
  metadata?: {
    fileName: string;
    klydoDurationInSeconds?: number;
    relatedCalendarEventId?: string;
    chimesFileName?: string;
    chimesDurationInSeconds?: number;
    loopUrlChecksum?: string;
    alarmFileName?: string;
    alarmDurationInSeconds?: number;
    previewFrameSeconds?: number | null;
  };
  /**
   * The public facet. `status` is THE public serving gate, stored not computed:
   * `true` means the klydo is served publicly RIGHT NOW. Approval opens it
   * immediately unless the requested date is still in the future, in which case an
   * hourly cron flips it — so never derive it from a date on the client.
   * `reviewStatus` absent = the review was never recorded.
   */
  public: {
    status: boolean;
    reviewStatus?: KlydoReviewState;
  };
  /**
   * Banned by an admin (the backoffice "Unlist" switch). Independent of the
   * review status, which a ban never changes. The studio shows "Banned" instead
   * of the review status while this is true.
   */
  unlisted?: boolean;
  /** The admin's note to the artist while `unlisted` is true. */
  unlistedMessage?: string;
  /** Epoch ms of the last ban / un-ban. */
  unlistedAt?: number;
  klydoAnimation?: KlydoAnimationSizes;
  chimesUrls?: string[];
  strikesUrls?: string[];
  alarmUrls?: string[];
  photoUrl?: string;
  pendulumLogo?: string | null;
  stats: {
    favorites: number;
    viewed: number;
    time: number;
  };
  capabilitiesV2?: {
    animationSound: {
      mode: SoundMode;
      playOnceConfig?: {
        frequency?: number;
        random?: boolean;
      };
    };
    alarm?: {
      type: AlarmType;
    };
  };
  collectionId?: string;
};

/**
 * The klydo's parent collection, attached by the api's `$lookup` (whole
 * document, no projection). `reviewStatus` is the collection's OWN review — the
 * klydo carries no collection review field of its own, so this is where the
 * gallery's collection status row reads from.
 */
export type CollectionInfo = {
  id: string;
  name: string;
  coverUrl?: string;
  logoUrl?: string;
  type?: CollectionType;
  reviewStatus?: CollectionReviewStatus;
  marketplace?: MarketplaceInfo;
};

export type PopulatedKlydo = Klydo & {
  collection?: CollectionInfo;
};

/**
 * Partial write payload for the draft-upsert endpoint.
 *
 * `public` is deliberately NOT part of it. The api strips the whole facet from
 * this route's body (`StudioKlydo.controller`, `delete safe.public`) and types it
 * away too, because `public.status` is the public serving gate: an artist who
 * could set it on their own draft would get a genuinely servable klydo the moment
 * that draft joined an approved collection, with no review. Publication goes
 * through request-publication and publication-date instead.
 */
export type KlydoUpdate = Partial<Omit<Klydo, 'public'>>;

export type KlydoApiClient = {
  processImage: (loopUrl: string) => Promise<Theme[]>;
  checkContrast: (filePath: string, handColor: string) => Promise<string>;
  search: (
    query: string,
    options: KlydoSearchOptions,
  ) => Promise<PopulatedKlydo[]>;
  searchCollectionCandidates: (
    query: string,
    options: KlydoSearchOptions,
  ) => Promise<PopulatedKlydo[]>;
  deleteKlydo: (klydoId: string) => Promise<void>;
  upsertDraftKlydo: (klydoId: string, klydo: KlydoUpdate) => Promise<void>;
  requestPublication: (
    klydoId: string,
    publicationTimestamp: number,
    comment?: string,
  ) => Promise<void>;
  withdrawKlydo: (klydoId: string) => Promise<void>;
  setPublicationDate: (
    klydoId: string,
    publicationTimestamp: number,
  ) => Promise<void>;
  getKlydo: (id: string) => Promise<PopulatedKlydo>;
  getKlydosByPublicationDate: (dateTime: Date) => Promise<Klydo[]>;
};

export type KlydoSearchFilter = { excludeIds: string[] };

export type KlydoSearchOptions = {
  limit: number;
  skip: number;
  collectionId?: string;
  filters?: KlydoSearchFilters;
};

const klydoApiClient: KlydoApiClient = {
  processImage: async (loopUrl: string) => {
    return baseClient.post({
      url: `/klydos/process-image`,
      body: {
        loopUrl: loopUrl,
      },
    });
  },

  checkContrast: async (filePath: string, handColor: string) => {
    return baseClient.post<string>({
      url: `/klydos/check-contrast`,
      body: {
        filePath: filePath,
        handColor: handColor,
      },
    });
  },
  search: async (query: string, options: KlydoSearchOptions) => {
    return baseClient.post({
      url: `/studio/klydos/.search/text`,
      body: { query, options },
    });
  },
  // Despite the name, this is the endpoint that backs the collection picker and
  // it does NOT restrict results to sealed klydos — the route name is the legacy
  // one, pinned by the api. What narrows the result set is the
  // `availableForCollection` filter flag the caller sends.
  searchCollectionCandidates: async (
    query: string,
    options: KlydoSearchOptions,
  ) => {
    return baseClient.post({
      url: `/studio/klydos/.search/collection-candidates`,
      body: { query, options },
    });
  },
  deleteKlydo: async (klydoId: string) => {
    return baseClient.delete({ url: `/studio/klydos/${klydoId}` });
  },
  upsertDraftKlydo: async (klydoId: string, klydo: KlydoUpdate) => {
    return baseClient.put({
      url: `/studio/klydos/${klydoId}`,
      body: klydo,
    });
  },
  // Opens (or replaces) the artist's PUBLICATION REQUEST. The endpoint stamps the
  // review record as pending server-side, so the client must not supply a state.
  //
  // The desired go-live date is the WHOLE payload: the api reads the stored klydo
  // itself and validates that (name, loopUrl and the five `theme` colours),
  // returning the same 400 message as before. Klydo content sent here would be
  // ignored — so the klydo must already be SAVED before this is called, or the
  // request is validated against stale content.
  requestPublication: async (
    klydoId: string,
    publicationTimestamp: number,
    // Stored here and nowhere else; an empty box clears the reviewer's note.
    comment?: string,
  ) => {
    // The date is always sent. The api also accepts an omitted key (keep a stored
    // date) and an explicit null (submit with no date), but the studio always
    // submits through the publish modal, which always produces one — so those two
    // paths had no caller and were removed rather than left as untested surface.
    return baseClient.put({
      url: `/studio/klydos/${klydoId}/request-publication`,
      body: { publicationTimestamp, comment },
    });
  },
  withdrawKlydo: async (klydoId: string) => {
    // Clears the serving gate but keeps the review record.
    // No body — the endpoint takes the owner from the token.
    return baseClient.put({
      url: `/studio/klydos/${klydoId}/withdraw`,
    });
  },
  setPublicationDate: async (klydoId: string, publicationTimestamp: number) => {
    // Reschedules the publication of a klydo whose PUBLICATION REQUEST is already
    // approved: now/past = public immediately, future = scheduled. Not a way to
    // publish without a review — the api requires
    // `public.reviewStatus.state === approved` and 400s otherwise.
    //
    // The api also accepts `null` here, to cancel a scheduled publication, but the
    // studio has no UI that can reach it: `number` is deliberate, not an oversight.
    // Taking a klydo down is an admin action in backoffice.
    return baseClient.put({
      url: `/studio/klydos/${klydoId}/publication-date`,
      body: { publicationTimestamp },
    });
  },
  getKlydo(id: string) {
    return baseClient.get<PopulatedKlydo>({
      url: `/studio/klydos/${id}`,
    });
  },
  getKlydosByPublicationDate: async (dateTime: Date) => {
    return baseClient.get<Klydo[]>({
      url: `/studio/klydos/by-publication-date`,
      params: {
        year: dateTime.getFullYear(),
        month: dateTime.getMonth() + 1,
        day: dateTime.getDate(),
      },
    });
  },
};

export default klydoApiClient;
